import { DateTimePicker } from "@/components/ui/datetime";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useRef } from "react";
import { MdClear } from "react-icons/md";

interface DateOnlyProps {
    value: string | number | { seconds: number; nanoseconds?: number } | null;
    onChange?: (date: Date | null) => void;
}

export const DateOnly = ({ value, onChange }: DateOnlyProps) => {
    const [date, setDate] = useState<Date | null>(null);
    const [isParsed, setIsParsed] = useState(false);
    const expectedTimestampRef = useRef<number | null>(null);

    const handleOnChange = (date: Date | null) => {
        if (!date) {
            console.log("Invalid date");
            setIsParsed(false);
            return;
        }
        // Set time to midnight for date-only
        date.setHours(0, 0, 0, 0);
        setDate(date);
        setIsParsed(true);
        onChange?.(date);
    };

    const handleClearDate = () => {
        expectedTimestampRef.current = null;
        setDate(null);
        setIsParsed(false);
        onChange?.(null);
    };

    const handleSetToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const timestamp = Math.floor(today.getTime() / 1000);
        expectedTimestampRef.current = timestamp;
        console.log('[DateOnly] Setting today:', today, 'timestamp:', timestamp);
        setDate(today);
        setIsParsed(true);
        onChange?.(today);
    };

    useEffect(() => {
        try {
            if (value === null || value === 0) {
                if (expectedTimestampRef.current === null) {
                    setIsParsed(false);
                    setDate(null);
                }
                return;
            }
            
            let parsedDate: Date;
            
            if (value && typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
                parsedDate = new Date(value.seconds * 1000);
                console.log('[DateOnly] Parsed timestamp object:', { seconds: value.seconds, date: parsedDate.toISOString() });
            } else if (typeof value === "string") {
                if (value.includes('T') || value.match(/^\d{4}-\d{2}-\d{2}/)) {
                    parsedDate = new Date(value);
                } else {
                    const timestampSeconds = parseInt(value, 10);
                    if (isNaN(timestampSeconds)) {
                        throw new Error("Invalid timestamp string");
                    }
                    parsedDate = new Date(timestampSeconds * 1000);
                }
            } else if (typeof value === "number") {
                let timestampMs: number;
                if (value > 1e12) {
                    timestampMs = value;
                    console.warn('[DateOnly] Value appears to be in milliseconds:', value, 'Treating as milliseconds');
                } else {
                    timestampMs = value * 1000;
                }
                parsedDate = new Date(timestampMs);
                console.log('[DateOnly] Parsed number timestamp:', { 
                    value, 
                    treatedAs: value > 1e12 ? 'milliseconds' : 'seconds',
                    timestampMs,
                    date: parsedDate.toISOString(),
                    dateString: parsedDate.toLocaleDateString()
                });
            } else {
                throw new Error(`Invalid value type: ${typeof value}, value: ${JSON.stringify(value)}`);
            }
            
            // Set time to midnight for date-only
            parsedDate.setHours(0, 0, 0, 0);
            
            console.log('[DateOnly] useEffect - value:', value, 'parsedDate:', parsedDate.toISOString());
            
            if (!isNaN(parsedDate.getTime())) {
                const propTimestamp = Math.floor(parsedDate.getTime() / 1000);

                if (expectedTimestampRef.current !== null && expectedTimestampRef.current === propTimestamp) {
                    expectedTimestampRef.current = null;
                    return;
                }

                if (expectedTimestampRef.current !== null) {
                    return;
                }

                setIsParsed(true);
                setDate(parsedDate);
            } else {
                throw new Error("Invalid Date");
            }
        } catch (error) {
            console.error('[DateOnly] Error parsing date value:', value, error);
            if (expectedTimestampRef.current === null) {
                setIsParsed(false);
                setDate(null);
            }
        }
    }, [value]);

    return (
        <div className="flex items-center" style={{ zIndex: 99999 }}>
            {isParsed ? (
                <div className="flex flex-row items-center gap-2">
                    <div className="w-auto">
                        <DateTimePicker
                            key={`date-${date?.getTime()}`}
                            value={date ?? undefined}
                            onChange={(date) => handleOnChange(date || null)}
                            granularity="day"
                            className="w-auto"
                        />
                    </div>
                    <Button
                        onClick={handleClearDate}
                        variant="outline"
                        size="sm"
                        type="button"
                    >
                        <MdClear />
                    </Button>
                </div>
            ) : (
                <div className="flex flex-row items-center gap-2">
                    <div className="w-auto">
                        <DateTimePicker
                            key="date-empty"
                            value={undefined}
                            granularity="day"
                            onChange={(date) => handleOnChange(date || null)}
                            className="w-auto"
                        />
                    </div>
                    <Button
                        onClick={handleSetToday}
                        variant="outline"
                        size="sm"
                        type="button"
                    >
                        Today
                    </Button>
                </div>
            )}
        </div>
    );
};

